/**
 * +field: read-only lookups into DungeonDef.field shared by the field system and the AI (field mobs
 * have no pack: their aggro radius, wander area and leash come from their spawn point).
 */
import type { ContentRegistry, DungeonDef, FieldAreaDef, FieldDef, FieldSpawnDef, PackDef, Vec2 } from '../contracts';
import { FIELD_AGGRO_RADIUS } from '../contracts';

interface FieldIndex {
  spawns: Map<string, FieldSpawnDef>;
  /** Synthetic loose "pack" per spawn point, so the AI's per-mob aggro / call-for-help code applies. */
  packs: Map<string, PackDef>;
}

const cache = new WeakMap<DungeonDef, FieldIndex>();

function index(d: DungeonDef): FieldIndex {
  let idx = cache.get(d);
  if (!idx) {
    idx = { spawns: new Map(), packs: new Map() };
    for (const s of d.field?.spawns ?? []) {
      idx.spawns.set(s.id, s);
      idx.packs.set(s.id, { id: `field:${s.id}`, roomId: '', spawns: [], aggroRadius: s.aggroRadius ?? FIELD_AGGRO_RADIUS, link: 'loose' });
    }
    cache.set(d, idx);
  }
  return idx;
}

/** The spawn point definition of a field mob (undefined off field maps). */
export function fieldSpawnDef(content: Pick<ContentRegistry, 'dungeon'>, dungeonId: string, spawnId: string): FieldSpawnDef | undefined {
  const d = content.dungeon(dungeonId);
  return d?.field ? index(d).spawns.get(spawnId) : undefined;
}

/** Synthetic loose PackDef for a field spawn point (aggroRadius from the spawn; 0 = passive). */
export function fieldPackDef(content: Pick<ContentRegistry, 'dungeon'>, dungeonId: string, spawnId: string): PackDef | undefined {
  const d = content.dungeon(dungeonId);
  return d?.field ? index(d).packs.get(spawnId) : undefined;
}

/** Even-odd point in polygon (sim yards). */
function inPoly(p: Vec2, pts: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/**
 * +zone: the named area at `p` (the first in definition order containing it: sub-areas before
 * their region), or null outside every area / on maps without areas.
 */
export function fieldAreaAt(field: Pick<FieldDef, 'areas'> | undefined, p: Vec2): FieldAreaDef | null {
  for (const a of field?.areas ?? []) if (inPoly(p, a.shape.points)) return a;
  return null;
}

/** +zone: the region (top-level area) at `p`: the area itself, or its parent. */
export function fieldRegionAt(field: Pick<FieldDef, 'areas'> | undefined, p: Vec2): FieldAreaDef | null {
  const a = fieldAreaAt(field, p);
  if (!a?.region) return a;
  return field?.areas?.find((x) => x.id === a.region) ?? a;
}

/** +zone: the level band that applies at `p` (its area's, else the map's). */
export function fieldLevelAt(field: Pick<FieldDef, 'areas' | 'levelBand'>, p: Vec2): [number, number] {
  const a = fieldAreaAt(field, p);
  if (a?.levelBand) return a.levelBand;
  const r = a?.region ? field.areas?.find((x) => x.id === a.region) : undefined;
  return r?.levelBand ?? field.levelBand;
}
